"use client";

import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@bi-studio/shared/hooks";
import { AppLayout, LoadingScreen, ErrorState } from "@bi-studio/shared/components";
import { useForm } from "@mantine/form";
import type {
    Chart,
    Report,
    GridPosition,
    DashboardItem,
    ReportQueryResult,
    DashboardUpdate,
} from "@bi-studio/shared/lib";
import {
    updateDashboard,
    addDashboardItem,
    removeDashboardItem,
    updateDashboardItem,
    executeReport,
    getFolderForItem,
    getDashboard,
} from "@bi-studio/shared/apis";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
    fetchDashboardDetails,
    fetchAvailableComponents,
    resetDashboardState,
    selectDashboardFull,
} from "@/store/slices/dashboardSlice";
import { PageActions } from "@/components/common";
import { DashboardEditActionsButton } from "@/components/page-action-buttons";
import { DashboardEditView } from "@/components/dashboards";

export default function EditDashboardPage() {
    const { isAuthenticated, isLoading: authLoading, token } = useAuth();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { id: dashboardId } = useParams();

    const {
        dashboard,
        availableCharts: charts,
        availableReports: reports,
        isLoading,
        isLoadingComponents,
        error: reduxError
    } = useAppSelector(selectDashboardFull);

    const [isSaving, setIsSaving] = React.useState(false);
    const [localError, setLocalError] = React.useState<string | null>(null);
    const error = localError || reduxError;
    
    // Layout and items state
    const [selectedCharts, setSelectedCharts] = React.useState<Chart[]>([]);
    const [selectedReports, setSelectedReports] = React.useState<Report[]>([]);
    const [chartLayouts, setChartLayouts] = React.useState<Map<string, GridPosition>>(new Map());
    const [reportLayouts, setReportLayouts] = React.useState<Map<string, GridPosition>>(new Map());
    const [existingItems, setExistingItems] = React.useState<Map<string, DashboardItem>>(new Map());
    const [reportData, setReportData] = React.useState<Map<string, ReportQueryResult>>(new Map());
    const [loadingReports, setLoadingReports] = React.useState<Set<string>>(new Set());

    const form = useForm<DashboardUpdate>({
        initialValues: {
            name: "",
            description: "",
            allowedRoles: [],
        },
        validate: {
            name: (value) => (value?.trim() ? null : "Dashboard name is required"),
        },
    });

    const handleLogout = React.useCallback(() => {
        navigate("/login");
    }, [navigate]);

    // Load initial data
    React.useEffect(() => {
        if (!token || !dashboardId) return;
        dispatch(fetchDashboardDetails({ token, dashboardId }));
        dispatch(fetchAvailableComponents(token));
        
        return () => {
            dispatch(resetDashboardState());
        };
    }, [token, dashboardId, dispatch]);

    // Populate local states when dashboard and components are loaded
    React.useEffect(() => {
        if (dashboard && charts.length > 0 && reports.length > 0) {
            // Populate form
            form.setValues({
                name: dashboard.name || "",
                description: dashboard.description || "",
                allowedRoles: dashboard.allowedRoles || [],
            });
            form.resetDirty();

            // Process existing items
            if (dashboard.items && dashboard.items.length > 0) {
                const itemsMap = new Map<string, DashboardItem>();
                const chartLayoutsMap = new Map<string, GridPosition>();
                const reportLayoutsMap = new Map<string, GridPosition>();
                const matchedCharts: Chart[] = [];
                const matchedReports: Report[] = [];

                dashboard.items.forEach((item) => {
                    const itemKey = item.chartId || item.reportId;
                    if (itemKey) {
                        itemsMap.set(itemKey, item);
                        if (item.layout) {
                            if (item.chartId) {
                                chartLayoutsMap.set(item.chartId, item.layout);
                                const chart = charts.find(c => c.id === item.chartId);
                                if (chart) matchedCharts.push(chart);
                            } else if (item.reportId) {
                                reportLayoutsMap.set(item.reportId, item.layout);
                                const report = reports.find(r => r.id === item.reportId);
                                if (report) matchedReports.push(report);
                            }
                        }
                    }
                });

                setExistingItems(itemsMap);
                setChartLayouts(chartLayoutsMap);
                setReportLayouts(reportLayoutsMap);
                setSelectedCharts(matchedCharts);
                setSelectedReports(matchedReports);
            }
        }
    }, [dashboard, charts, reports]);

    // Load report data for selected reports
    React.useEffect(() => {
        if (!token || selectedReports.length === 0) return;

        const loadReportData = async (report: Report) => {
            if (reportData.has(report.id) || loadingReports.has(report.id)) return;

            try {
                setLoadingReports((prev) => new Set(prev).add(report.id));
                const data = await executeReport(token, report.id, {
                    pagination: { page: 1, pageSize: 50 },
                });
                setReportData((prev) => new Map(prev).set(report.id, data));
            } catch (err) {
                console.error(`Failed to load data for report ${report.id}:`, err);
            } finally {
                setLoadingReports((prev) => {
                    const next = new Set(prev);
                    next.delete(report.id);
                    return next;
                });
            }
        };

        selectedReports.forEach(loadReportData);
    }, [token, selectedReports, reportData, loadingReports]);

    const handleSave = async () => {
        if (form.validate().hasErrors) return;
        if (!token || !dashboardId) return;

        try {
            setIsSaving(true);
            setLocalError(null);

            // Update dashboard metadata
            await updateDashboard(token, dashboardId, form.values);

            // Sync items: determine what to add, update, or remove
            const currentItemsById = new Map<string, DashboardItem>();
            const freshDashboard = await getDashboard(token, dashboardId);
            (freshDashboard.items || []).forEach((item) => {
                const itemKey = item.chartId || item.reportId;
                if (itemKey) currentItemsById.set(itemKey, item);
            });

            // 1. Remove items no longer selected
            for (const [_, item] of existingItems.entries()) {
                const stillSelected = 
                    (item.chartId && selectedCharts.find(c => c.id === item.chartId)) ||
                    (item.reportId && selectedReports.find(r => r.id === item.reportId));
                
                if (!stillSelected && item.id) {
                    await removeDashboardItem(token, dashboardId, item.id);
                }
            }

            // 2. Add or update charts
            for (const chart of selectedCharts) {
                const layout = chartLayouts.get(chart.id) || { x: 0, y: 0, w: 6, h: 4 };
                const existing = currentItemsById.get(chart.id);

                if (existing && existing.id) {
                    if (JSON.stringify(existing.layout) !== JSON.stringify(layout)) {
                        await updateDashboardItem(token, dashboardId, existing.id, {
                            chartId: chart.id,
                            layout,
                        });
                    }
                } else {
                    await addDashboardItem(token, dashboardId, {
                        chartId: chart.id,
                        layout,
                    });
                }
            }

            // 3. Add or update reports
            for (const report of selectedReports) {
                const layout = reportLayouts.get(report.id) || { x: 0, y: 0, w: 6, h: 4 };
                const existing = currentItemsById.get(report.id);

                if (existing && existing.id) {
                    if (JSON.stringify(existing.layout) !== JSON.stringify(layout)) {
                        await updateDashboardItem(token, dashboardId, existing.id, {
                            reportId: report.id,
                            layout,
                        });
                    }
                } else {
                    await addDashboardItem(token, dashboardId, {
                        reportId: report.id,
                        layout,
                    });
                }
            }

            navigate(`/dashboards/${dashboardId}`);
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : "Failed to update dashboard");
            setIsSaving(false);
        }
    };

    const handleCancel = async () => {
        if (!token || !dashboardId) {
            navigate("/folders");
            return;
        }
        try {
            const folderInfo = await getFolderForItem(token, "dashboard", dashboardId);
            navigate(folderInfo.folderId ? `/folders/${folderInfo.folderId}` : `/dashboards/${dashboardId}`);
        } catch {
            navigate(`/dashboards/${dashboardId}`);
        }
    };

    if (authLoading || isLoading || isLoadingComponents) {
        return (
            <AppLayout onLogout={handleLogout} title="Edit Dashboard" subtitle="Workspace">
                <LoadingScreen message="Loading dashboard details..." />
            </AppLayout>
        );
    }

    if (!isAuthenticated) return null;

    return (
        <AppLayout onLogout={handleLogout} title="Edit Dashboard" subtitle="Workspace">
            <div className="space-y-6">
                <PageActions 
                    title="Edit Dashboard"
                    onBack={handleCancel}
                >
                    <DashboardEditActionsButton
                        handleCancel={handleCancel}
                        handleSave={handleSave}
                        isSaving={isSaving}
                    />
                </PageActions>

                {error && (
                    <ErrorState 
                        title="Failed to update dashboard"
                        message={error}
                        onAction={() => setLocalError(null)}
                    />
                )}

                <DashboardEditView 
                    isSaving={isSaving}
                    form={form}
                    charts={charts}
                    reports={reports}
                    selectedCharts={selectedCharts}
                    setSelectedCharts={setSelectedCharts}
                    selectedReports={selectedReports}
                    setSelectedReports={setSelectedReports}
                    chartLayouts={chartLayouts}
                    setChartLayouts={setChartLayouts}
                    reportLayouts={reportLayouts}
                    setReportLayouts={setReportLayouts}
                    reportData={reportData}
                    setReportData={setReportData}
                    loadingReports={loadingReports}
                    token={token || ""}
                />
            </div>
        </AppLayout>
    );
}
